/**
 * Appointment Types for MEV-Med System
 * TypeScript interfaces for appointment management
 *
 * @version 2025.1.0
 */

import type { AppointmentExam } from './appointmentExam';
import type { AppointmentReferral } from './appointmentReferral';

/**
 * Enum for Appointment exam types
 */
export enum AppointmentExamType {
  ADMISSIONAL = 'Admissional',
  DEMISSIONAL = 'Demissional',
  PERIODICO = 'Periódico',
  COMPLEMENTAR = 'Complementar',
  RETORNO_TRABALHO = 'Retorno ao Trabalho',
  MUDANCA_RISCO = 'Mudança de Risco Ocupacional',
  CONSULTA_CLINICA = 'Consulta Clínica',
  /** Lei 13.103/2015 — motorista profissional. Gera S-2221, nunca S-2220. */
  TOXICOLOGICO = 'Toxicológico'
}

/**
 * Enum for Appointment status
 */
export enum AppointmentStatus {
  PENDENTE = 'PENDENTE',
  AGUARDANDO_APROVACAO = 'AGUARDANDO_APROVACAO',
  CONFIRMADO = 'CONFIRMADO',
  AGUARDANDO_LAUDOS = 'AGUARDANDO_LAUDOS',
  CONCLUIDO = 'CONCLUIDO',
  CANCELADO = 'CANCELADO',
  REJEITADO = 'REJEITADO',
  REAGENDAR = 'REAGENDAR'
}

/**
 * Enum for Appointment confirmation status
 */
export enum AppointmentConfirmation {
  SIM = 'SIM',
  NAO = 'NAO',
  PRESENTE = 'PRESENTE',
  AUSENTE = 'AUSENTE'
}

/**
 * Employee summary for appointment display
 */
export interface AppointmentEmployee {
  id: number;
  name: string;
  cpf?: string;
  companyId?: number;
  departmentId?: number;
  occupationId?: number;
  department?: { id: number; name: string };
  occupation?: { id: number; name: string };
}

/**
 * Company summary for appointment display
 */
export interface AppointmentCompany {
  id: number;
  tradeName?: string;
  legalName?: string;
}

/**
 * Clinic/Provider summary for appointment display
 */
export interface AppointmentClinic {
  id: number;
  tradeName?: string;
  legalName?: string;
  address?: string;
  addressNumber?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  providerAddress?: {
    address?: string;
    addressNumber?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    complement?: string;
  };
}

/**
 * Doctor summary for appointment display
 */
export interface AppointmentDoctor {
  id: number;
  name?: string;
}

/**
 * Appointment interface
 */
export interface Appointment {
  id: number;
  employeeId: number;
  companyId: number;
  doctorId: number | null;
  clinicId: number | null;
  examType: string;
  specialActivity: string | null;
  otherExam: string | null;
  selectedDate: string;
  scheduleSlotId: number | null;
  hour: string | null;
  minute: string | null;
  responsibleEmail: string | null;
  responsiblePhone: string | null;
  status: string;
  confirmation: string | null;
  address: string | null;
  notice: string | null;
  examValidity: string | null;
  appliedExamId: number | null;
  asoNotes: string | null;
  observation: string | null;
  pdfPath: string | null;
  guidePdfPath: string | null;
  medicalRecordPdfPath: string | null;
  presenceEmail: boolean;
  sendEmail: boolean;
  sendWhatsApp: boolean;
  // Cancellation reason
  cancellationReason: string | null;
  // Ordem de Chegada (walk-in)
  period: string | null;
  createdAt: string;
  updatedAt: string;

  // Approval fields
  approvedById: number | null;
  approvedAt: string | null;
  rejectedById: number | null;
  rejectedAt: string | null;
  rejectionReason: string | null;

  // Out-of-slot tracking
  isOutOfSlot: boolean;
  outOfSlotBookedById: number | null;
  outOfSlotReason: string | null;
  outOfSlotBookedAt: string | null;

  // Attendance fields
  attendanceStatus?: string;
  attendanceType?: string;
  presenceConfirmedAt?: string | null;
  attendanceStartedAt?: string | null;
  attendanceCompletedAt?: string | null;
  queueNumber?: number | null;
  queueCalledAt?: string | null;
  receptionistId?: number | null;

  // Notification tracking
  professionalNotifiedAt: string | null;
  clinicNotifiedAt: string | null;

  // Relations (when included)
  employee?: AppointmentEmployee;
  company?: AppointmentCompany;
  clinic?: AppointmentClinic;
  doctor?: AppointmentDoctor;
  approvedBy?: { id: number; name: string };
  rejectedBy?: { id: number; name: string };
  outOfSlotBookedBy?: { id: number; name: string };
  receptionist?: { id: number; name: string };
  scheduleSlot?: {
    id: number;
    doctorName?: string;
    location?: string;
    slotDate?: string;
    startTime?: string;
    endTime?: string;
    status?: string;
    maxCapacity?: number;
    currentBookings?: number;
  };

  // Complementary exams
  appointmentExams?: AppointmentExam[];

  // Referrals grouped by clinic
  referrals?: AppointmentReferral[];
}

/**
 * Appointment response from API (with formatted fields)
 */
export interface AppointmentResponse {
  id: number;
  employeeId: number;
  employeeName: string;
  employeeCpf: string | null;
  companyId: number;
  companyName: string;
  clinicId: number | null;
  clinicName: string | null;
  doctorId: number | null;
  doctorName: string | null;
  examType: string;
  selectedDate: string;
  hour: string | null;
  minute: string | null;
  status: string;
  confirmation: string | null;
  address: string | null;
  observation: string | null;
  period: string | null;
  appointmentExams?: AppointmentExam[];
  createdAt: string;
}

/**
 * Create appointment request
 */
export interface CreateAppointmentRequest {
  employeeId: number;
  companyId: number;
  examType: string;
  selectedDate: string;
  doctorId?: number;
  clinicId?: number;
  scheduleSlotId?: number;
  hour?: string;
  minute?: string;
  responsibleEmail?: string;
  responsiblePhone?: string;
  specialActivity?: string;
  otherExam?: string;
  address?: string;
  notice?: string;
  examValidity?: string;
  appliedExamId?: number;
  asoNotes?: string;
  observation?: string;
  sendEmail?: boolean;
  sendWhatsApp?: boolean;
  // Out-of-slot booking
  isOutOfSlot?: boolean;
  outOfSlotReason?: string;
  // Complementary exams
  examIds?: number[];
  examMetadata?: Array<{ examId: number; applicationLevel?: string | null; periodicityMonths?: number | null }>;
  // Exam referrals (encaminhamentos para clínicas terceirizadas)
  examReferrals?: Array<{ examId: number; referralClinicId: number }>;
  // Ordem de Chegada (walk-in)
  period?: string;
}

/**
 * Update appointment request
 */
export interface UpdateAppointmentRequest {
  selectedDate?: string;
  hour?: string;
  minute?: string;
  examType?: string;
  status?: string;
  confirmation?: string;
  doctorId?: number;
  clinicId?: number;
  scheduleSlotId?: number;
  responsibleEmail?: string;
  responsiblePhone?: string;
  specialActivity?: string;
  otherExam?: string;
  address?: string;
  notice?: string;
  examValidity?: string;
  appliedExamId?: number;
  asoNotes?: string;
  observation?: string;
  sendEmail?: boolean;
  sendWhatsApp?: boolean;
}

/**
 * Appointment filters for listing
 */
export interface AppointmentFilters {
  search?: string;
  companyId?: number;
  employeeId?: number;
  clinicId?: number;
  doctorId?: number;
  status?: string | string[];
  confirmation?: string;
  examType?: string | string[];
  startDate?: string;
  endDate?: string;
}

/**
 * Appointment list parameters
 */
export interface AppointmentListParams extends AppointmentFilters {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

/**
 * Appointment list response
 */
export interface AppointmentListResponse {
  appointments: AppointmentResponse[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Quantity by date response (for calendar)
 */
export interface AppointmentQuantityByDate {
  date: string;
  count: number;
}

/**
 * Form data for appointment creation/editing
 */
export interface AppointmentFormData {
  employeeId: number | null;
  companyId: number | null;
  examType: string;
  selectedDate: string;
  doctorId: number | null;
  clinicId: number | null;
  hour: string;
  minute: string;
  responsibleEmail: string;
  responsiblePhone: string;
  address: string;
  notice: string;
  observation: string;
  sendEmail: boolean;
}

/**
 * Status badge colors mapping
 */
export const APPOINTMENT_STATUS_COLORS: Record<AppointmentStatus, 'default' | 'primary' | 'success' | 'error' | 'warning'> = {
  [AppointmentStatus.PENDENTE]: 'warning',
  [AppointmentStatus.AGUARDANDO_APROVACAO]: 'warning',
  [AppointmentStatus.CONFIRMADO]: 'primary',
  [AppointmentStatus.AGUARDANDO_LAUDOS]: 'warning',
  [AppointmentStatus.CONCLUIDO]: 'success',
  [AppointmentStatus.CANCELADO]: 'error',
  [AppointmentStatus.REJEITADO]: 'error',
  [AppointmentStatus.REAGENDAR]: 'default',
};

/**
 * Status labels for display
 */
export const APPOINTMENT_STATUS_LABELS: Record<AppointmentStatus, string> = {
  [AppointmentStatus.PENDENTE]: 'Pendente',
  [AppointmentStatus.AGUARDANDO_APROVACAO]: 'Aguardando Aprovação',
  [AppointmentStatus.CONFIRMADO]: 'Confirmado',
  [AppointmentStatus.AGUARDANDO_LAUDOS]: 'Aguardando Laudos',
  [AppointmentStatus.CONCLUIDO]: 'Concluído',
  [AppointmentStatus.CANCELADO]: 'Cancelado',
  [AppointmentStatus.REJEITADO]: 'Rejeitado',
  [AppointmentStatus.REAGENDAR]: 'Reagendar',
};

/**
 * Exam type labels for display
 */
export const EXAM_TYPE_LABELS: Record<AppointmentExamType, string> = {
  [AppointmentExamType.ADMISSIONAL]: 'Admissional',
  [AppointmentExamType.DEMISSIONAL]: 'Demissional',
  [AppointmentExamType.PERIODICO]: 'Periódico',
  [AppointmentExamType.COMPLEMENTAR]: 'Complementar',
  [AppointmentExamType.RETORNO_TRABALHO]: 'Retorno ao Trabalho',
  [AppointmentExamType.MUDANCA_RISCO]: 'Mudança de Risco',
  [AppointmentExamType.CONSULTA_CLINICA]: 'Consulta Clínica',
  [AppointmentExamType.TOXICOLOGICO]: 'Toxicológico',
};

/**
 * Confirmation labels for display
 */
export const CONFIRMATION_LABELS: Record<AppointmentConfirmation, string> = {
  [AppointmentConfirmation.SIM]: 'Confirmado',
  [AppointmentConfirmation.NAO]: 'Não Confirmado',
  [AppointmentConfirmation.PRESENTE]: 'Presente',
  [AppointmentConfirmation.AUSENTE]: 'Ausente',
};

// ============================================================================
// APPROVAL WORKFLOW TYPES
// ============================================================================

/**
 * Approve appointment request
 */
export interface ApproveAppointmentRequest {
  notes?: string;
}

/**
 * Reject appointment request
 */
export interface RejectAppointmentRequest {
  reason: string;
}

/**
 * Pending appointment response (with extra fields for approval UI)
 */
export interface PendingAppointmentResponse extends AppointmentResponse {
  isOutOfSlot: boolean;
  outOfSlotReason: string | null;
  outOfSlotBookedByName: string | null;
  createdAt: string;
}

/**
 * Pending appointments list response
 */
export interface PendingAppointmentsListResponse {
  appointments: PendingAppointmentResponse[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Approval action result
 */
export interface ApprovalActionResult {
  success: boolean;
  message: string;
  appointment: Appointment;
}

// ============================================================================
// ORDEM DE CHEGADA (WALK-IN) TYPES
// ============================================================================

/**
 * Clinic linked to a company (for clinic selector)
 */
export interface LinkedClinic {
  id: number;
  tradeName: string | null;
  legalName: string;
  displayName: string;
  serviceType: string | null;
  morningCapacity: number | null;
  afternoonCapacity: number | null;
  morningStartTime: string | null;
  morningEndTime: string | null;
  afternoonStartTime: string | null;
  afternoonEndTime: string | null;
  defaultSlotDuration: number | null;
  concurrentCapacity: number | null;
  city: string | null;
  state: string | null;
  neighborhood: string | null;
  address: string | null;
  addressNumber: string | null;
  zipCode: string | null;
  providerNotice: string | null;
}

/**
 * Period availability for Ordem de Chegada
 */
export interface PeriodAvailability {
  capacity: number;
  booked: number;
  available: number;
  startTime: string | null;
  endTime: string | null;
}

/**
 * Virtual slot for Hora Marcada
 */
export interface ClinicScheduleSlot {
  time: string;       // "HH:MM"
  capacity: number;
  booked: number;
  available: number;
}

/**
 * Ordem de Chegada availability response
 */
export interface OrdemChegadaAvailability {
  morning: PeriodAvailability;
  afternoon: PeriodAvailability;
}
